import { useQuery } from '@tanstack/react-query';
import { Loader2, ShoppingCart } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { cn } from '@/shared/lib/cn';
import { GarantiaBadge } from '@/features/ventas/components/Garantia';
import { listMantenimientoDetalles, type MantenimientoRow } from '../api';

/**
 * Lightweight read-only detalles view for the cart icon on a
 * Mantenimiento row. Matches PowerApps `Popup_Details` — just the
 * purchased items with qty. Mantenimiento is post-sale: the team
 * needs to know what was sold, NOT the financial values, so monetary
 * columns/totals are intentionally hidden here. The full venta
 * detail (with money) lives behind the Eye icon → DetalleVentaPage.
 */
export function VentaDetallesDialog({
  row,
  onClose,
}: {
  row: MantenimientoRow;
  onClose: () => void;
}) {
  const detallesQ = useQuery({
    queryKey: ['mantenimiento', 'detalles', row.id],
    queryFn: () => listMantenimientoDetalles(row.id),
    staleTime: 30_000,
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-muted-foreground" aria-hidden />
            Detalle venta Nº {row.id}
          </DialogTitle>
          <DialogDescription>
            {row.fields.Cliente_RO || '—'}
            {row.fields.FechaPago_RO ? ` · Pago ${row.fields.FechaPago_RO}` : ''}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {detallesQ.isPending ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
            </div>
          ) : detallesQ.isError ? (
            <div className="py-6 text-center text-sm text-destructive">
              No se pudo cargar el detalle.
            </div>
          ) : (detallesQ.data ?? []).length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Sin líneas activas.
            </div>
          ) : (
            <>
              {/* Desktop / tablet table — qty only, no money columns. */}
              <div className="hidden overflow-hidden rounded-lg border md:block">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <Th>Artículo</Th>
                      <Th className="text-right">Cantidad</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(detallesQ.data ?? []).map((d) => (
                      <tr key={d.id}>
                        <Td>
                          <div className="font-medium text-foreground">
                            {d.articulo}
                            {d.esRegalo && (
                              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                                Regalo
                              </span>
                            )}
                            {d.llevaGarantia && <span className="ml-2 inline-block align-middle"><GarantiaBadge linea={d} /></span>}
                          </div>
                          {d.nroSerie && (
                            <div className="text-[11px] text-muted-foreground">
                              N° Serie: {d.nroSerie}
                            </div>
                          )}
                        </Td>
                        <Td className="text-right tabular-nums">{d.cantidad}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards — qty only, no money. */}
              <ul className="overflow-hidden rounded-lg border bg-card md:hidden">
                {(detallesQ.data ?? []).map((d, idx) => (
                  <li
                    key={d.id}
                    className={cn(
                      'flex items-start justify-between gap-2 px-3 py-3 text-sm',
                      idx > 0 && 'border-t',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="break-words font-medium text-foreground">
                        {d.articulo}
                        {d.esRegalo && (
                          <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                            Regalo
                          </span>
                        )}
                        {d.llevaGarantia && <span className="ml-2 inline-block align-middle"><GarantiaBadge linea={d} /></span>}
                      </div>
                      {d.nroSerie && (
                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                          N° Serie: {d.nroSerie}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 text-xs text-muted-foreground">
                      Cant.{' '}
                      <span className="tabular-nums text-foreground">
                        {d.cantidad}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Th({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <th className={cn('px-3 py-2 font-semibold', className)}>{children}</th>;
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={cn('px-3 py-2 align-top', className)}>{children}</td>;
}
